import { useState, useRef } from 'react';
import { uploadFile } from '../api/client';

interface ReviewerUploadControlProps {
  submissionId: string;
  currentAttachmentUrl: string | null;
  onUploadSuccess: (newUrl: string) => void;
}

export function ReviewerUploadControl({ submissionId, currentAttachmentUrl, onUploadSuccess }: ReviewerUploadControlProps) {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const fileInputRef = useRef<HTMLInputElement>(null);

  async function handleUpload() {
    const file = fileInputRef.current?.files?.[0];
    if (!file) return;

    // Warn if replacing an existing file
    if (currentAttachmentUrl) {
      const confirmed = window.confirm(
        'Uploading a file will replace the Original/Existing File. Are you sure you want to continue?'
      );
      if (!confirmed) return;
    }

    // Client-side validation
    const allowedTypes = [
      'application/pdf',
      'application/vnd.openxmlformats-officedocument.wordprocessingml.document',
      'image/png',
      'image/jpeg',
    ];
    if (!allowedTypes.includes(file.type)) {
      setError('Invalid file type. Allowed: PDF, DOCX, PNG, JPG.');
      return;
    }
    if (file.size > 10 * 1024 * 1024) {
      setError('File too large. Maximum size is 10 MB.');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const data = await uploadFile<{ attachmentUrl: string }>(
        `/weekend-booklet/submissions/${submissionId}/reviewer-upload`,
        file,
        'file',
      );
      setSuccess('File uploaded successfully!');
      onUploadSuccess(data.attachmentUrl);
      if (fileInputRef.current) fileInputRef.current.value = '';
    } catch (err: unknown) {
      const apiErr = err as { message?: string };
      setError(apiErr.message || 'Upload failed. Please try again.');
    } finally {
      setUploading(false);
    }
  }

  return (
    <div style={{ marginTop: '0.75rem' }}>
      <h5 style={{ fontSize: '0.8rem', color: '#374151', marginBottom: '0.4rem', fontWeight: 600 }}>Upload File</h5>

      {currentAttachmentUrl && (
        <div style={{ marginBottom: '0.5rem', fontSize: '0.8rem' }}>
          <span style={{ color: '#6b7280' }}>Current: </span>
          <a
            href={`${import.meta.env.VITE_API_URL || (typeof window !== 'undefined' ? window.location.origin + '/api' : 'http://localhost:3000')}/public/uploads/${currentAttachmentUrl.split('/').pop()}`}
            target="_blank"
            rel="noopener noreferrer"
            style={{ color: '#1a6b3c', textDecoration: 'underline' }}
          >
            {currentAttachmentUrl.split('/').pop()}
          </a>
        </div>
      )}

      <div style={{ display: 'flex', alignItems: 'center', gap: '0.5rem', marginBottom: '0.25rem' }}>
        <input
          ref={fileInputRef}
          type="file"
          accept=".pdf,.docx,.png,.jpg,.jpeg"
          disabled={uploading}
          style={{ fontSize: '0.8rem', flex: 1 }}
          aria-label="Select file to upload"
        />
        <button
          onClick={handleUpload}
          disabled={uploading}
          style={{
            padding: '0.35rem 0.7rem',
            backgroundColor: uploading ? '#9ca3af' : '#1a6b3c',
            color: '#fff',
            border: 'none',
            borderRadius: '4px',
            fontSize: '0.8rem',
            fontWeight: 600,
            cursor: uploading ? 'not-allowed' : 'pointer',
            whiteSpace: 'nowrap',
          }}
        >
          {uploading ? 'Uploading...' : 'Upload'}
        </button>
      </div>

      <p style={{ fontSize: '0.7rem', color: '#9ca3af', margin: '0 0 0.25rem 0' }}>
        Accepted: PDF, DOCX, PNG, JPG — Max 10 MB
      </p>

      {error && (
        <p style={{ fontSize: '0.75rem', color: '#dc2626', margin: '0.25rem 0 0 0' }}>{error}</p>
      )}
      {success && (
        <p style={{ fontSize: '0.75rem', color: '#16a34a', margin: '0.25rem 0 0 0' }}>{success}</p>
      )}
    </div>
  );
}
